import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Loader2, User, TrendingUp, Briefcase, Target, Check } from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function AssignmentSuggester({ targetAccountIds, onSelectRep, onSkip }) {
    const { getAuthHeader } = useAuth();
    const [loading, setLoading] = useState(true);
    const [suggestions, setSuggestions] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchSuggestions = async () => {
            if (!targetAccountIds?.length) return;
            setLoading(true);
            setError(null);
            try {
                const res = await axios.post(`${API}/api/admin/suggest-assignment`, {
                    target_account_ids: targetAccountIds,
                }, { headers: getAuthHeader() });
                setSuggestions(res.data.suggestions || []);
            } catch (err) {
                setError('Failed to get AI recommendations');
                console.error(err);
            }
            finally { setLoading(false); }
        };
        fetchSuggestions();
    }, [targetAccountIds, getAuthHeader]);

    const scoreColor = (score) => {
        if (score >= 80) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
        if (score >= 60) return 'bg-amber-100 text-amber-700 border-amber-200';
        return 'bg-stone-100 text-stone-600 border-stone-200';
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-8">
                <div className="relative">
                    <Sparkles className="w-8 h-8 text-amber-400 animate-pulse" />
                    <Loader2 className="w-5 h-5 text-stone-400 animate-spin absolute -bottom-1 -right-1" />
                </div>
                <p className="text-sm text-stone-500 mt-3">AI is analyzing rep profiles and performance...</p>
                <p className="text-xs text-stone-400 mt-1">Matching {targetAccountIds.length} target{targetAccountIds.length > 1 ? 's' : ''} to your sales team</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="text-center py-6">
                <p className="text-sm text-stone-500 mb-3">{error}</p>
                <Button variant="outline" size="sm" onClick={onSkip}>Skip to Manual Assignment</Button>
            </div>
        );
    }

    if (suggestions.length === 0) {
        return (
            <div className="text-center py-6">
                <p className="text-sm text-stone-500 mb-3">No recommendations available</p>
                <Button variant="outline" size="sm" onClick={onSkip}>Proceed to Manual Assignment</Button>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-amber-500" />
                <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider">AI Recommended Assignments</h4>
            </div>

            {suggestions.map(suggestion => (
                <div key={suggestion.target_account_id} className="bg-white border border-stone-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                        <h5 className="text-sm font-semibold text-stone-900">{suggestion.company_name}</h5>
                        <span className="text-xs text-stone-400">{suggestion.recommended_reps?.length || 0} candidates</span>
                    </div>

                    <div className="space-y-2">
                        {(suggestion.recommended_reps || []).map((rep, idx) => (
                            <button
                                key={rep.user_id}
                                onClick={() => onSelectRep(suggestion.target_account_id, rep.user_id, rep.name)}
                                className="w-full flex items-center gap-3 p-3 rounded-lg border border-stone-100 hover:border-stone-300 hover:bg-stone-50 transition-colors text-left group"
                            >
                                <div className="flex-shrink-0">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                                        idx === 0 ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-600'
                                    }`}>
                                        {rep.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                    </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-stone-900">{rep.name}</span>
                                        {idx === 0 && (
                                            <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Best Match</span>
                                        )}
                                    </div>
                                    <p className="text-xs text-stone-500 mt-0.5 truncate">{rep.reasoning}</p>
                                </div>
                                <div className="flex-shrink-0 flex items-center gap-2">
                                    <span className={`text-xs px-2 py-1 rounded-full font-semibold border ${scoreColor(rep.score)}`}>
                                        {rep.score}%
                                    </span>
                                    <Check className="w-4 h-4 text-stone-300 group-hover:text-stone-600 transition-colors" />
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            ))}

            <div className="flex justify-end pt-2">
                <Button variant="outline" size="sm" onClick={onSkip}>
                    Skip AI — Assign Manually
                </Button>
            </div>
        </div>
    );
}
